import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { TaskCard, BoardWithColumns } from '../types/board';

interface UseTaskCardsRealtimeProps {
  boardId: string | null;
  boardData: BoardWithColumns | null;
  setBoardData: React.Dispatch<React.SetStateAction<BoardWithColumns | null>>;
  onError?: (error: string) => void;
  onConnectionChange?: (connected: boolean) => void;
}

export function useTaskCardsRealtime({ 
  boardId, 
  boardData, 
  setBoardData, 
  onError,
  onConnectionChange
}: UseTaskCardsRealtimeProps) {
  
  // Use refs to store callback functions to avoid dependency issues
  const onErrorRef = useRef(onError);
  const onConnectionChangeRef = useRef(onConnectionChange);
  
  // Update refs when callbacks change
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  
  useEffect(() => {
    onConnectionChangeRef.current = onConnectionChange;
  }, [onConnectionChange]);
  
  const handleInsert = useCallback((payload: any) => {
    const newCard = payload.new as TaskCard;
    console.log('🆕 Task card created:', newCard);
    
    setBoardData(prevData => {
      if (!prevData) {
        console.log('⚠️ No boardData available, skipping insert update');
        return null;
      }
      
      console.log('📝 Adding new card to board data');
      return {
        ...prevData,
        columns: prevData.columns.map(column => {
          if (column.id === newCard.column_id) {
            // Add new card to the column, maintaining sort order by position
            const updatedCards = [...column.cards, newCard].sort((a, b) => a.position - b.position);
            console.log(`➕ Added card "${newCard.title}" to column "${column.title}"`);
            return {
              ...column,
              cards: updatedCards
            };
          }
          return column;
        })
      };
    });
  }, [setBoardData]);

  const handleUpdate = useCallback((payload: any) => {
    const updatedCard = payload.new as TaskCard;
    const oldCard = payload.old as TaskCard;
    
    console.log('📝 Task card updated:', {
      id: updatedCard.id,
      title: updatedCard.title,
      oldStatus: oldCard.status,
      newStatus: updatedCard.status,
      oldColumn: oldCard.column_id,
      newColumn: updatedCard.column_id
    });
    
    setBoardData(prevData => {
      if (!prevData) {
        console.log('⚠️ No boardData available, skipping update');
        return null;
      }
      
      let newColumns = [...prevData.columns];
      
      // If card moved to a different column
      if (oldCard.column_id !== updatedCard.column_id) {
        console.log(`🔄 Moving card "${updatedCard.title}" between columns`);
        
        // Check if card is already in the target column (optimistic update case)
        const targetColumn = newColumns.find(col => col.id === updatedCard.column_id);
        const cardAlreadyInTarget = targetColumn?.cards.some(card => card.id === updatedCard.id);
        
        if (cardAlreadyInTarget) {
          console.log(`⚠️ Card "${updatedCard.title}" already in target column, just updating data`);
          // Card is already in target column (from optimistic update), just update its data
          newColumns = newColumns.map(column => {
            if (column.id === updatedCard.column_id) {
              return {
                ...column,
                cards: column.cards.map(card => 
                  card.id === updatedCard.id ? updatedCard : card
                ).sort((a, b) => a.position - b.position)
              };
            }
            return column;
          });
        } else {
          console.log(`🚚 Actually moving card "${updatedCard.title}" from column to column`);
        // Remove from old column
        newColumns = newColumns.map(column => {
          if (column.id === oldCard.column_id) {
            return {
              ...column,
              cards: column.cards.filter(card => card.id !== updatedCard.id)
            };
          }
          return column;
        });
        
        // Add to new column
        newColumns = newColumns.map(column => {
          if (column.id === updatedCard.column_id) {
            const updatedCards = [...column.cards, updatedCard].sort((a, b) => a.position - b.position);
            return {
              ...column,
              cards: updatedCards
            };
          }
          return column;
        });
        }
      } else {
        console.log(`📝 Updating card "${updatedCard.title}" in same column`);
        // Update in the same column
        newColumns = newColumns.map(column => {
          if (column.id === updatedCard.column_id) {
            const updatedCards = column.cards
              .map(card => card.id === updatedCard.id ? updatedCard : card)
              .sort((a, b) => a.position - b.position);
            return {
              ...column,
              cards: updatedCards
            };
          }
          return column;
        });
      }
      
      return {
        ...prevData,
        columns: newColumns
      };
    });
  }, [setBoardData]);

  const handleDelete = useCallback((payload: any) => {
    const deletedCard = payload.old as TaskCard;
    console.log('🗑️ Task card deleted:', deletedCard.id);
    
    setBoardData(prevData => {
      if (!prevData) {
        console.log('⚠️ No boardData available, skipping delete');
        return null;
      }
      
      console.log(`🗑️ Removing card "${deletedCard.title}" from board`);
      return {
        ...prevData,
        columns: prevData.columns.map(column => ({
          ...column,
          cards: column.cards.filter(card => card.id !== deletedCard.id)
        }))
      };
    });
  }, [setBoardData]);

  useEffect(() => {
    if (!supabase || !boardId) {
      console.log('⏸️ Skipping task cards subscription - missing supabase or boardId');
      onConnectionChangeRef.current?.(false);
      return;
    }

    console.log('🔄 Setting up task cards real-time subscription for board:', boardId);

    try {
      const channel = supabase
        .channel(`task-cards-${boardId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'task_cards',
            filter: `board_id=eq.${boardId}`
          },
          handleInsert
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'task_cards',
            filter: `board_id=eq.${boardId}`
          },
          handleUpdate
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'task_cards',
            filter: `board_id=eq.${boardId}`
          },
          handleDelete
        )
        .subscribe((status) => {
          console.log('📡 Task cards subscription status:', status);
          
          if (status === 'SUBSCRIBED') {
            console.log('✅ Successfully subscribed to task cards changes for board:', boardId);
            onConnectionChangeRef.current?.(true);
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Task cards channel error');
            onConnectionChangeRef.current?.(false);
            onErrorRef.current?.('Real-time connection error');
          } else if (status === 'TIMED_OUT') {
            console.error('❌ Task cards subscription timed out');
            onConnectionChangeRef.current?.(false);
            onErrorRef.current?.('Real-time connection timed out');
          } else if (status === 'CLOSED') {
            console.log('🔒 Task cards subscription closed');
            onConnectionChangeRef.current?.(false);
          }
        });

      return () => {
        console.log('🧹 Cleaning up task cards subscription for board:', boardId);
        channel.unsubscribe().then(() => {
          console.log('✅ Successfully unsubscribed from task cards');
        }).catch((error) => {
          console.error('❌ Error unsubscribing from task cards:', error);
        });
      };
    } catch (error) {
      console.error('❌ Error setting up task cards subscription:', error);
      onErrorRef.current?.('Error setting up real-time connection');
      onConnectionChangeRef.current?.(false);
    }
  }, [boardId, handleInsert, handleUpdate, handleDelete]); // Removed onError and onConnectionChange from dependencies
} 